// client/src/coms/personalisation/layoutModel.js
// Layout presets and preference inheritance:
//   1. account-wide prefs (prefsSchema DEFAULT_PREFS shape, stored on the server)
//   2. per-server overrides: a flat { 'dot.path': value } of only what the user customised for that server
//   3. temporary session changes (the same flat shape; layout previews, "just for now" tweaks)
// A preset only decides structure (which panels show, where, spacing). Sizes, text scale and theme are never
// part of a preset, so choosing "Focus" for one server does not reset the user's message size everywhere.
import { DEFAULT_PREFS, sanitizePrefs, sanitizeServerOverrides } from './prefsSchema';

export const LAYOUT_PRESET_INFO = [
  { id: 'classic', label: 'Classic', description: 'The familiar Voxemera layout: channels left, members right.' },
  { id: 'compact', label: 'Compact', description: 'Tighter spacing and condensed messages so more of the conversation fits.' },
  { id: 'focus', label: 'Focus', description: 'Just the conversation. Side panels, tabs and banners are hidden.' },
  { id: 'social', label: 'Social', description: 'Members, tabs and your voice and activity controls always within reach.' },
  { id: 'creator', label: 'Creator', description: 'Room for Canvas, Watch Together and files, with chat open beside activities.' },
  { id: 'custom', label: 'Custom', description: 'Your own arrangement.' },
];

// Structural fields each preset sets (layout paths relative to prefs.layout).
export const PRESET_STRUCTURE = {
  classic: {
    'sidebar.visible': true,
    'sidebar.side': 'left',
    'members.visible': true,
    'members.side': 'right',
    userBar: 'bottom',
    tabBar: 'auto',
    headerBar: true,
    activitiesButton: true,
    activityPlacement: 'main',
    density: 'comfortable',
    chatDensity: 'cozy',
  },
  compact: {
    'sidebar.visible': true,
    'sidebar.side': 'left',
    'members.visible': true,
    'members.side': 'right',
    userBar: 'bottom',
    tabBar: 'auto',
    headerBar: true,
    activitiesButton: true,
    activityPlacement: 'main',
    density: 'compact',
    chatDensity: 'compact',
  },
  focus: {
    'sidebar.visible': false,
    'sidebar.side': 'left',
    'members.visible': false,
    'members.side': 'right',
    userBar: 'bottom',
    tabBar: 'never',
    headerBar: false,
    activitiesButton: false,
    activityPlacement: 'main',
    density: 'comfortable',
    chatDensity: 'cozy',
  },
  social: {
    'sidebar.visible': true,
    'sidebar.side': 'left',
    'members.visible': true,
    'members.side': 'right',
    userBar: 'top',
    tabBar: 'always',
    headerBar: true,
    activitiesButton: true,
    activityPlacement: 'main',
    density: 'comfortable',
    chatDensity: 'cozy',
  },
  creator: {
    'sidebar.visible': true,
    'sidebar.side': 'left',
    'members.visible': false,
    'members.side': 'right',
    userBar: 'bottom',
    tabBar: 'always',
    headerBar: false,
    activitiesButton: true,
    activityPlacement: 'beside-chat',
    density: 'compact',
    chatDensity: 'cozy',
  },
};

export const STRUCTURAL_PATHS = Object.keys(PRESET_STRUCTURE.classic);

export function clone(v) {
  return JSON.parse(JSON.stringify(v));
}

export function getPath(obj, path) {
  return String(path)
    .split('.')
    .reduce((o, k) => (o && typeof o === 'object' ? o[k] : undefined), obj);
}

export function setPath(obj, path, value) {
  const keys = String(path).split('.');
  const out = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  let cur = out;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const k = keys[i];
    cur[k] = cur[k] && typeof cur[k] === 'object' ? { ...cur[k] } : {};
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return out;
}

function applyStructure(layout, presetId) {
  const structure = PRESET_STRUCTURE[presetId];
  if (!structure) return layout;
  let out = layout;
  for (const [p, v] of Object.entries(structure)) out = setPath(out, p, v);
  return out;
}

// Choosing a preset for the account: copies its structure into the layout.
export function applyPresetToLayout(layout, presetId) {
  const base = layout || clone(DEFAULT_PREFS.layout);
  if (presetId === 'custom') return { ...base, preset: 'custom' };
  return { ...applyStructure(base, presetId), preset: presetId };
}

// After editing one structural field the layout no longer matches its preset.
export function markCustomIfChanged(layout, path) {
  if (!STRUCTURAL_PATHS.includes(path) || layout.preset === 'custom') return layout;
  const preset = PRESET_STRUCTURE[layout.preset];
  if (preset && getPath(layout, path) !== preset[path]) return { ...layout, preset: 'custom' };
  return layout;
}

function applyFlatLayer(layout, layer) {
  if (!layer) return layout;
  let out = layout;
  if (layer['layout.preset'] && layer['layout.preset'] !== 'custom') {
    out = { ...applyStructure(out, layer['layout.preset']), preset: layer['layout.preset'] };
  }
  for (const [key, value] of Object.entries(layer)) {
    if (!key.startsWith('layout.') || key === 'layout.preset' || value === undefined) continue;
    out = setPath(out, key.slice('layout.'.length), value);
  }
  return out;
}

// -> { layout, themeChoice, nav: { landingChannel }, notifications: { badges }, sources: { path: 'global'|'server'|'session' } }
export function resolveEffective({ global, serverOverrides, temp } = {}) {
  const g = sanitizePrefs(global || {});
  const ov = sanitizeServerOverrides(serverOverrides || {});
  const tmp = temp && typeof temp === 'object' ? temp : {};
  let layout = clone(g.layout);
  layout = applyFlatLayer(layout, ov);
  layout = applyFlatLayer(layout, tmp);
  const sources = {};
  for (const key of Object.keys(ov)) sources[key] = 'server';
  for (const key of Object.keys(tmp)) sources[key] = 'session';
  return {
    layout,
    theme: g.theme,
    themeChoice: tmp['theme.choice'] || ov['theme.choice'] || 'global',
    nav: { landingChannel: ov['nav.landingChannel'] || '' },
    notifications: { badges: ov['notifications.badges'] || 'all' },
    themeSuggestionSeen: !!ov['theme.suggestionSeen'],
    sources,
  };
}

// The value a server setting would inherit from the account (for "Inherited: …" hints and per-field reset).
export function inheritedValue(global, key) {
  const g = sanitizePrefs(global || {});
  if (key === 'theme.choice') return 'global';
  if (key === 'nav.landingChannel') return '';
  if (key === 'notifications.badges') return 'all';
  if (key.startsWith('layout.')) return getPath(g.layout, key.slice('layout.'.length));
  return undefined;
}
